import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { toast } from "sonner";

const CartContext = createContext(null);

/* =========================================================
   HOOK
========================================================= */

export const useCart = () => {
  const context = useContext(CartContext);

  if (!context) {
    throw new Error(
      "useCart must be used inside CartProvider"
    );
  }

  return context;
};

/* =========================================================
   HELPERS
========================================================= */

const normalizeSize = (size) => {
  if (
    size === null ||
    size === undefined
  ) {
    return "";
  }

  return String(size)
    .trim()
    .toLowerCase();
};

const toNumber = (value, fallback = 0) => {
  const number = Number(value);

  return Number.isFinite(number)
    ? number
    : fallback;
};

/* =========================================================
   FIND VARIANT
========================================================= */

const getSelectedVariant = (
  product,
  size
) => {
  if (!product) {
    return null;
  }

  const variants = Array.isArray(
    product.variants
  )
    ? product.variants
    : [];

  if (!variants.length) {
    return null;
  }

  const normalizedSize =
    normalizeSize(size);

  return (
    variants.find(
      (variant) =>
        normalizeSize(
          variant?.size
        ) === normalizedSize
    ) || null
  );
};

/* =========================================================
   GET AVAILABLE STOCK
========================================================= */

const getAvailableStock = (
  product,
  size
) => {
  if (!product) {
    return null;
  }

  const variants = Array.isArray(
    product.variants
  )
    ? product.variants
    : [];

  /*
   * If variants exist, variant stock is
   * authoritative.
   */
  if (variants.length > 0) {
    const selectedVariant =
      getSelectedVariant(
        product,
        size
      );

    /*
     * A product with variants requires
     * a valid variant/size.
     */
    if (!selectedVariant) {
      return 0;
    }

    return Math.max(
      0,
      toNumber(
        selectedVariant.stock,
        0
      )
    );
  }

  /*
   * Product-level stock tracking.
   */
  if (
    product.stock_tracking === true
  ) {
    return Math.max(
      0,
      toNumber(
        product.stock_quantity,
        0
      )
    );
  }

  /*
   * Explicitly unavailable.
   */
  if (
    product.in_stock === false
  ) {
    return 0;
  }

  /*
   * Stock is not being tracked.
   */
  return null;
};

/* =========================================================
   GET PRICE
========================================================= */

const getItemPrice = (
  product,
  selectedVariant
) => {
  /*
   * Variant price has priority.
   */
  if (
    selectedVariant &&
    selectedVariant.price !==
      null &&
    selectedVariant.price !==
      undefined
  ) {
    return Math.max(
      0,
      toNumber(
        selectedVariant.price,
        0
      )
    );
  }

  /*
   * Product price fallback.
   */
  return Math.max(
    0,
    toNumber(
      product?.price,
      0
    )
  );
};

/* =========================================================
   CART KEY
========================================================= */

const createCartKey = (
  productId,
  size
) => {
  return `${String(
    productId
  ).trim()}::${normalizeSize(
    size
  ) || "default"}`;
};

/* =========================================================
   NORMALIZE SAVED ITEM
========================================================= */

const normalizeSavedItem = (
  item
) => {
  if (!item || typeof item !== "object") {
    return null;
  }

  const productId =
    item.product_id ||
    item.productId ||
    item.id;

  if (!productId) {
    return null;
  }

  const size =
    item.size ??
    item.selectedVariant?.size ??
    item.variant?.size ??
    null;

  const selectedVariant =
    item.selectedVariant ||
    item.variant ||
    null;

  const price =
    selectedVariant?.price !==
      undefined &&
    selectedVariant?.price !==
      null
      ? toNumber(
          selectedVariant.price,
          0
        )
      : toNumber(
          item.price,
          0
        );

  const quantity = Math.max(
    1,
    toNumber(
      item.quantity,
      1
    )
  );

  return {
    ...item,

    key:
      item.key ||
      createCartKey(
        productId,
        size
      ),

    product_id:
      productId,

    name:
      item.name ||
      item.product_name ||
      "Product",

    price,

    image:
      item.image ||
      item.images?.[0] ||
      item.product_image ||
      "",

    images:
      Array.isArray(item.images)
        ? item.images
        : [],

    size,

    selectedVariant,

    variant:
      selectedVariant,

    school_id:
      item.school_id ||
      "",

    school_name:
      item.school_name ||
      "",

    category:
      item.category ||
      "",

    stock:
      item.stock === null ||
      item.stock === undefined
        ? null
        : toNumber(
            item.stock,
            0
          ),

    quantity,
  };
};

/* =========================================================
   PROVIDER
========================================================= */

export const CartProvider = ({
  children,
}) => {
  /* =======================================================
     LOAD CART
  ======================================================= */

  const [items, setItems] =
    useState(() => {
      try {
        const saved =
          localStorage.getItem(
            "uc_cart"
          );

        if (!saved) {
          return [];
        }

        const parsed =
          JSON.parse(saved);

        if (
          !Array.isArray(parsed)
        ) {
          return [];
        }

        /*
         * Normalize old cart entries.
         */
        const normalized =
          parsed
            .map(
              normalizeSavedItem
            )
            .filter(Boolean);

        return normalized;
      } catch (error) {
        console.error(
          "Could not load cart:",
          error
        );

        return [];
      }
    });

  const [open, setOpen] =
    useState(false);

  /* =======================================================
     SAVE CART
  ======================================================= */

  useEffect(() => {
    try {
      localStorage.setItem(
        "uc_cart",
        JSON.stringify(items)
      );
    } catch (error) {
      console.error(
        "Could not save cart:",
        error
      );
    }
  }, [items]);

  /* =======================================================
     ADD ITEM
  ======================================================= */

  const addItem = (
    product,
    size = null,
    quantity = 1
  ) => {
    if (!product) {
      toast.error(
        "This product cannot be added to the cart."
      );

      return false;
    }

    const productId =
      product.id ||
      product.product_id;

    if (!productId) {
      toast.error(
        "This product cannot be added to the cart."
      );

      return false;
    }

    const variants = Array.isArray(
      product.variants
    )
      ? product.variants
      : [];

    /*
     * If variants exist, a size is required.
     */
    if (
      variants.length > 0 &&
      !size
    ) {
      toast.error(
        "Please select a size."
      );

      return false;
    }

    const selectedVariant =
      getSelectedVariant(
        product,
        size
      );

    /*
     * Selected size must exist.
     */
    if (
      variants.length > 0 &&
      !selectedVariant
    ) {
      toast.error(
        "The selected size is unavailable."
      );

      return false;
    }

    const safeQuantity =
      Math.max(
        1,
        Math.floor(
          toNumber(
            quantity,
            1
          )
        )
      );

    const availableStock =
      getAvailableStock(
        product,
        size
      );

    /*
     * Validate stock for new item.
     */
    if (
      availableStock !== null &&
      safeQuantity >
        availableStock
    ) {
      toast.error(
        availableStock === 0
          ? "This item is currently out of stock"
          : `Only ${availableStock} available in stock`
      );

      return false;
    }

    const schoolName =
      product.school_name ||
      product.school?.name ||
      "";

    const key =
      createCartKey(
        productId,
        size
      );

    const price =
      getItemPrice(
        product,
        selectedVariant
      );

    setItems(
      (previousItems) => {
        const existingItem =
          previousItems.find(
            (item) =>
              item.key === key
          );

        /* =================================================
           EXISTING ITEM
        ================================================= */

        if (existingItem) {
          const currentQuantity =
            Math.max(
              1,
              toNumber(
                existingItem.quantity,
                1
              )
            );

          const requestedQuantity =
            currentQuantity +
            safeQuantity;

          /*
           * Re-check stock using current
           * product information.
           */
          if (
            availableStock !== null &&
            requestedQuantity >
              availableStock
          ) {
            toast.error(
              availableStock === 0
                ? "This item is currently out of stock"
                : `Only ${availableStock} available in stock`
            );

            return previousItems;
          }

          return previousItems.map(
            (item) => {
              if (
                item.key !== key
              ) {
                return item;
              }

              return {
                ...item,

                quantity:
                  requestedQuantity,

                price,

                stock:
                  availableStock,

                selectedVariant:
                  selectedVariant ||
                  item.selectedVariant ||
                  null,

                variant:
                  selectedVariant ||
                  item.variant ||
                  null,
              };
            }
          );
        }

        /* =================================================
           NEW ITEM
        ================================================= */

        return [
          ...previousItems,

          {
            key,

            product_id:
              productId,

            name:
              product.name ||
              product.product_name ||
              "Product",

            price,

            image:
              product.image ||
              product.images?.[0] ||
              "",

            images:
              Array.isArray(
                product.images
              )
                ? product.images
                : [],

            size:
              size || null,

            selectedVariant:
              selectedVariant ||
              null,

            variant:
              selectedVariant ||
              null,

            school_id:
              product.school_id ||
              "",

            school_name:
              schoolName,

            category:
              product.category ||
              "",

            stock:
              availableStock,

            quantity:
              safeQuantity,
          },
        ];
      }
    );

    /*
     * Open drawer after successful add.
     */
    setOpen(true);

    return true;
  };

  /* =======================================================
     SET EXACT QUANTITY
  ======================================================= */

  const setQty = (
    key,
    quantity
  ) => {
    const requestedQuantity =
      Math.max(
        1,
        Math.floor(
          toNumber(
            quantity,
            1
          )
        )
      );

    setItems(
      (previousItems) =>
        previousItems.map(
          (item) => {
            if (
              item.key !== key
            ) {
              return item;
            }

            const stock =
              item.stock === null ||
              item.stock ===
                undefined ||
              item.stock === ""
                ? null
                : toNumber(
                    item.stock,
                    0
                  );

            if (
              stock !== null &&
              requestedQuantity >
                stock
            ) {
              toast.error(
                stock === 0
                  ? "This item is currently out of stock"
                  : `Only ${stock} available in stock`
              );

              return item;
            }

            return {
              ...item,

              quantity:
                requestedQuantity,
            };
          }
        )
    );
  };

  /* =======================================================
     UPDATE QUANTITY
  ======================================================= */

  const updateQty = (
    key,
    delta
  ) => {
    const safeDelta =
      Math.trunc(
        toNumber(
          delta,
          0
        )
      );

    if (!safeDelta) {
      return;
    }

    setItems(
      (previousItems) =>
        previousItems.map(
          (item) => {
            if (
              item.key !== key
            ) {
              return item;
            }

            const currentQuantity =
              Math.max(
                1,
                toNumber(
                  item.quantity,
                  1
                )
              );

            const newQuantity =
              Math.max(
                1,
                currentQuantity +
                  safeDelta
              );

            const stock =
              item.stock === null ||
              item.stock ===
                undefined ||
              item.stock === ""
                ? null
                : toNumber(
                    item.stock,
                    0
                  );

            if (
              stock !== null &&
              newQuantity >
                stock
            ) {
              toast.error(
                stock === 0
                  ? "This item is currently out of stock"
                  : `Only ${stock} available in stock`
              );

              return item;
            }

            return {
              ...item,

              quantity:
                newQuantity,
            };
          }
        )
    );
  };

  /* =======================================================
     REMOVE ITEM
  ======================================================= */

  const removeItem = (
    key
  ) => {
    setItems(
      (previousItems) =>
        previousItems.filter(
          (item) =>
            item.key !== key
        )
    );
  };

  /* =======================================================
     CLEAR CART
  ======================================================= */

  const clearCart = () => {
    setItems([]);
  };

  /* =======================================================
     TOTAL
  ======================================================= */

  const total = useMemo(() => {
    return items.reduce(
      (sum, item) => {
        const price =
          Math.max(
            0,
            toNumber(
              item.price,
              0
            )
          );

        const quantity =
          Math.max(
            0,
            toNumber(
              item.quantity,
              0
            )
          );

        return (
          sum +
          price * quantity
        );
      },
      0
    );
  }, [items]);

  /* =======================================================
     COUNT
  ======================================================= */

  const count = useMemo(() => {
    return items.reduce(
      (sum, item) =>
        sum +
        Math.max(
          0,
          toNumber(
            item.quantity,
            0
          )
        ),
      0
    );
  }, [items]);

  /* =======================================================
     CONTEXT VALUE
  ======================================================= */

  const value = {
    items,

    addItem,

    setQty,

    updateQty,

    removeItem,

    clearCart,

    total,

    count,

    open,

    setOpen,
  };

  /* =======================================================
     PROVIDER
  ======================================================= */

  return (
    <CartContext.Provider
      value={value}
    >
      {children}
    </CartContext.Provider>
  );
};

export default CartContext;
